import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  BookingListResponseSchema,
  BookingSchema,
  BookingSlotListResponseSchema,
  ResourceAvailabilitySchema,
  ResourceListResponseSchema,
  type Booking,
  type BookingStatus,
  type CreateBookingInput,
  type PatchBookingInput,
  type ResourceAvailability,
} from "@/api/types/booking";
import { InvoiceSchema, type Invoice } from "@/api/types/invoice";

/** Query-key factory — mirrors the events/invoices hook convention. */
export const bookingKeys = {
  all: ["bookings"] as const,
  resources: () => [...bookingKeys.all, "resources"] as const,
  slots: (kind?: string) => [...bookingKeys.all, "slots", kind ?? "all"] as const,
  availability: (resourceId: string | undefined, dateFrom: string, dateTo: string) =>
    [...bookingKeys.all, "availability", resourceId, dateFrom, dateTo] as const,
  lists: () => [...bookingKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) => [...bookingKeys.lists(), params] as const,
  details: () => [...bookingKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...bookingKeys.details(), id] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: bookingKeys.all });
}

export function useResources(activeOnly = true) {
  return useQuery({
    queryKey: bookingKeys.resources(),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/bookings/resources?active_only=${activeOnly}`);
      return validateResponse(ResourceListResponseSchema, data, "GET /v2/bookings/resources").items;
    },
    staleTime: 5 * 60 * 1000, // resources/prices rarely change
  });
}

export function useBookingSlots(kind?: string) {
  return useQuery({
    queryKey: bookingKeys.slots(kind),
    queryFn: async () => {
      const qs = kind ? `?kind=${kind}` : "";
      const { data } = await apiClient.get(`/v2/bookings/slots${qs}`);
      return validateResponse(BookingSlotListResponseSchema, data, "GET /v2/bookings/slots").items;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Availability for one resource across a date range — powers both the
 * calendar view and the create-booking form's live slot picker. */
export function useBookingAvailability(resourceId: string | undefined, dateFrom: string, dateTo: string) {
  return useQuery<ResourceAvailability>({
    queryKey: bookingKeys.availability(resourceId, dateFrom, dateTo),
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/v2/bookings/availability?resource_id=${resourceId}&date_from=${dateFrom}&date_to=${dateTo}`,
      );
      return validateResponse(ResourceAvailabilitySchema, data, "GET /v2/bookings/availability");
    },
    enabled: !!resourceId && !!dateFrom && !!dateTo,
    staleTime: 10 * 1000,
  });
}

interface UseBookingsParams {
  resource_id?: string;
  status?: BookingStatus;
  date_from?: string;
  date_to?: string;
  include_shadows?: boolean;
  limit?: number;
}

export function useBookings(params: UseBookingsParams = {}) {
  const { resource_id, status, date_from, date_to, include_shadows = true, limit = 50 } = params;

  return useInfiniteQuery({
    queryKey: bookingKeys.list({ resource_id, status, date_from, date_to, include_shadows, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (resource_id) searchParams.set("resource_id", resource_id);
      if (status) searchParams.set("status", status);
      if (date_from) searchParams.set("date_from", date_from);
      if (date_to) searchParams.set("date_to", date_to);
      searchParams.set("include_shadows", String(include_shadows));
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/bookings?${searchParams.toString()}`);
      return validateResponse(BookingListResponseSchema, data, "GET /v2/bookings");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 10 * 1000,
  });
}

export function useBooking(bookingId: string | undefined) {
  return useQuery<Booking>({
    queryKey: bookingKeys.detail(bookingId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/bookings/${bookingId}`);
      return validateResponse(BookingSchema, data, "GET /v2/bookings/:id");
    },
    enabled: !!bookingId,
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();
  return useMutation<Booking, unknown, CreateBookingInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/bookings", input);
      return validateResponse(BookingSchema, data, "POST /v2/bookings");
    },
    onSuccess: (booking) => {
      queryClient.setQueryData(bookingKeys.detail(booking.id), booking);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchBooking() {
  const queryClient = useQueryClient();
  return useMutation<Booking, unknown, PatchBookingInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/bookings/${id}`, patch);
      return validateResponse(BookingSchema, data, "PATCH /v2/bookings/:id");
    },
    onSuccess: (booking) => {
      queryClient.setQueryData(bookingKeys.detail(booking.id), booking);
      invalidateAll(queryClient);
    },
  });
}

function useBookingAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<Booking, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(`/v2/bookings/${id}/${endpoint}`, {});
      return validateResponse(BookingSchema, data, `POST /v2/bookings/:id/${label}`);
    },
    onSuccess: (booking) => {
      queryClient.setQueryData(bookingKeys.detail(booking.id), booking);
      invalidateAll(queryClient);
    },
  });
}

export function useConfirmBooking() {
  return useBookingAction("confirm", "confirm");
}

export function useCancelBooking() {
  return useBookingAction("cancel", "cancel");
}

export function useCompleteBooking() {
  return useBookingAction("complete", "complete");
}

/** Generates the booking's invoice through the existing invoice system —
 * returns the new Invoice (not a Booking); the caller re-fetches the
 * booking detail (invalidated below) to pick up the new `invoice_id`. */
export function useGenerateBookingInvoice() {
  const queryClient = useQueryClient();
  return useMutation<Invoice, unknown, string>({
    mutationFn: async (bookingId) => {
      const { data } = await apiClient.post(`/v2/bookings/${bookingId}/generate-invoice`, {});
      return validateResponse(InvoiceSchema, data, "POST /v2/bookings/:id/generate-invoice");
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}
